import React from 'react';
import { Card, Row, Col, Typography, Space, DatePicker, Select, Checkbox, Button, List, Tag } from 'antd';
import { analyticsAPI, supportAPI } from '../services/api';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';


const { Title } = Typography;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [range, setRange] = React.useState<{ start: Dayjs; end: Dayjs }>({ 
    start: dayjs().startOf('day'), 
    end: dayjs().endOf('day') 
  });
  const [preset, setPreset] = React.useState<string>('today');
  const [rememberDefault, setRememberDefault] = React.useState<boolean>(false);

  React.useEffect(() => {
    // Load saved preset/range if "remember" is enabled
    const remember = localStorage.getItem('dashboard_period_remember') === '1';
    if (!remember) return;
    const savedPreset = localStorage.getItem('dashboard_period_preset') || 'custom';
    const startStr = localStorage.getItem('dashboard_period_start');
    const endStr = localStorage.getItem('dashboard_period_end');
    if (startStr && endStr) {
      setRange({ start: dayjs(startStr), end: dayjs(endStr) });
      setPreset(savedPreset);
      setRememberDefault(true);
    }
  }, []);

  const persistIfNeeded = (pkey: string, r: { start: Dayjs; end: Dayjs }) => {
    if (rememberDefault) {
      localStorage.setItem('dashboard_period_remember', '1');
      localStorage.setItem('dashboard_period_preset', pkey);
      localStorage.setItem('dashboard_period_start', r.start.toISOString());
      localStorage.setItem('dashboard_period_end', r.end.toISOString());
    } else {
      localStorage.removeItem('dashboard_period_remember');
      localStorage.removeItem('dashboard_period_preset');
      localStorage.removeItem('dashboard_period_start');
      localStorage.removeItem('dashboard_period_end');
    }
  };

  const getRoleTitle = () => {
    switch (user?.role) {
      case 'admin':
        return 'Панель администратора';
      case 'customer':
        return 'Панель заказчика';
      case 'supplier':
        return 'Панель поставщика';
      case 'employee':
        return 'Личный кабинет';
      case 'support':
        return 'Кабинет службы поддержки';
      case 'delivery':
        return 'Панель доставщика';
      default:
        return 'Панель управления';
    }
  };

  const getRoleDescription = () => {
    switch (user?.role) {
      case 'admin':
        return 'Управление системой, пользователями и аналитика';
      case 'customer':
        return 'Управление сотрудниками, заказами и лимитами';
      case 'supplier':
        return 'Управление меню, заказами и документами';
      case 'employee':
        return 'Размещение заказов и просмотр истории';
      case 'support':
        return 'Обработка обращений пользователей';
      case 'delivery':
        return 'Управление доставками';
      default:
        return '';
    }
  };

  const [stats, setStats] = React.useState<{
    total_orders: number; total_orders_prev: number;
    active_users: number; active_users_prev: number;
    active_customers: number; active_customers_prev: number;
    active_suppliers: number; active_suppliers_prev: number;
    menu_items: number; menu_items_prev: number;
    total_amount: number; total_amount_prev: number;
    support_tickets_total: number; support_tickets_total_prev: number;
    support_tickets_unanswered: number; support_tickets_unanswered_prev: number;
    support_tickets_closed: number; support_tickets_closed_prev: number;
  } | null>(null);

  const [tickets, setTickets] = React.useState<any[]>([]);

  const loadStats = async () => {
    const startISO = range.start.toISOString();
    const endISO = range.end.toISOString();
    const data = await analyticsAPI.overview(startISO, endISO);
    setStats(data);
  };

  const loadTickets = async () => {
    if (user?.role !== 'admin' && user?.role !== 'support') return;
    try {
      const data = await supportAPI.getTickets({ archived: false });
      setTickets(data.slice(0, 10)); // последние 10
    } catch (error) {
      console.error('Failed to load tickets:', error);
    }
  };

  React.useEffect(() => { 
    loadStats(); 
    loadTickets(); 
  }, [range.start, range.end, user]);

  // Автообновление ленты поддержки каждые 15 сек
  React.useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'support') return;
    const id = setInterval(() => loadTickets(), 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const titleTextStyle: React.CSSProperties = { 
    fontSize: 14, 
    color: '#666', 
    whiteSpace: 'nowrap', 
    overflow: 'hidden', 
    textOverflow: 'ellipsis' 
  };
  const valueTextStyle: React.CSSProperties = { fontSize: 24, fontWeight: 600, whiteSpace: 'nowrap' };
  const valueAccentStyle: React.CSSProperties = { ...valueTextStyle, color: '#1890ff' };
  const rowLineStyle: React.CSSProperties = { 
    display: 'grid', 
    gridTemplateColumns: '1fr auto', 
    alignItems: 'center', 
    gap: 8 
  };
  const cardStyle: React.CSSProperties = { 
    height: 160, 
    display: 'flex', 
    flexDirection: 'column', 
    justifyContent: 'space-between' 
  };

  const Arrow: React.FC<{ curr?: number; prev?: number }> = ({ curr, prev }) => {
    if (curr === undefined || prev === undefined) return null;
    const up = (curr - prev) >= 0;
    return (
      <span style={{ color: up ? '#52c41a' : '#f5222d', marginLeft: 4, fontSize: 14, lineHeight: 1 }}>
        {up ? '▲' : '▼'}
      </span>
    );
  };

  const LiveTimer: React.FC<{ ticket: any }> = ({ ticket }) => {
    const [elapsed, setElapsed] = React.useState(0);
    const isClosed = ticket.status === 'closed' || ticket.status === 'answered';
    
    React.useEffect(() => {
      if (isClosed) return;
      const id = setInterval(() => {
        const baseTime = ticket.lastResponseAt ? new Date(ticket.lastResponseAt) : new Date(ticket.createdAt);
        const diff = Math.floor((Date.now() - baseTime.getTime()) / 1000);
        setElapsed(diff);
      }, 1000);
      return () => clearInterval(id);
    }, [ticket, isClosed]);
    
    if (isClosed) {
      return <Tag color="blue">{ticket.status === 'closed' ? 'Закрыто' : 'Отвечено'}</Tag>;
    }
    
    const remaining = 300 - elapsed;
    const urgent = remaining <= 0;
    const mins = Math.floor(Math.abs(remaining) / 60);
    const secs = Math.abs(remaining) % 60;
    return urgent ? 
      <Tag color="red">Просрочено {mins}:{secs < 10 ? '0' : ''}{secs}</Tag> : 
      <Tag color="green">Осталось {mins}:{secs < 10 ? '0' : ''}{secs}</Tag>;
  };

  return (
    <>
      <Row align="middle" justify="space-between" style={{ marginBottom: '24px' }}>
        <Col>
          <Title level={2} style={{ marginBottom: 4 }}>{getRoleTitle()}</Title>
          <p style={{ color: '#666', fontSize: '16px', margin: 0 }}>{getRoleDescription()}</p>
        </Col>
        <Col>
          <Space direction="vertical" size={4} style={{ alignItems: 'flex-end' }}>
            <Typography.Text type="secondary">Сегодня: {dayjs().format('DD.MM.YYYY')}</Typography.Text>
            <Select
              value={preset}
              onChange={(v) => {
                const now = dayjs();
                setPreset(v);
                let newRange;
                switch (v) {
                  case 'today':
                    newRange = { start: now.startOf('day'), end: now.endOf('day') };
                    break;
                  case 'this_week':
                    newRange = { start: now.startOf('week'), end: now.endOf('week') };
                    break;
                  case 'this_month':
                    newRange = { start: now.startOf('month'), end: now.endOf('month') };
                    break;
                  case 'last_month': {
                    const lm = now.subtract(1, 'month');
                    newRange = { start: lm.startOf('month'), end: lm.endOf('month') };
                    break;
                  }
                  case 'this_year':
                    newRange = { start: now.startOf('year'), end: now.endOf('year') };
                    break;
                  case 'last_year': {
                    const ly = now.subtract(1, 'year');
                    newRange = { start: ly.startOf('year'), end: ly.endOf('year') };
                    break;
                  }
                  default:
                    return;
                }
                setRange(newRange);
                persistIfNeeded(v, newRange);
              }}
              options={[
                { value: 'today', label: 'Сегодня' },
                { value: 'this_week', label: 'Эта неделя' },
                { value: 'this_month', label: 'Этот месяц' },
                { value: 'last_month', label: 'Прошлый месяц' },
                { value: 'this_year', label: 'Этот год' },
                { value: 'last_year', label: 'Прошлый год' },
                { value: 'custom', label: 'Произвольно' },
              ]}
              style={{ width: 200 }}
            />
            <DatePicker.RangePicker
              value={[range.start, range.end]}
              format="DD.MM.YYYY"
              onChange={(vals) => {
                if (vals && vals[0] && vals[1]) {
                  const r = { start: vals[0], end: vals[1] };
                  setRange(r);
                  setPreset('custom');
                  persistIfNeeded('custom', r);
                }
              }}
              allowClear={false}
            />
            <Checkbox 
              checked={rememberDefault} 
              onChange={(e) => {
                setRememberDefault(e.target.checked);
                persistIfNeeded(preset, range);
              }}
            >
              По умолчанию
            </Checkbox>
          </Space>
        </Col>
      </Row>

      {/* Статистика - первый ряд */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card style={cardStyle} styles={{ body: { padding: 16 } }}>
            <div style={rowLineStyle}>
              <span style={titleTextStyle}>Всего заказов</span>
              <span style={{ ...valueTextStyle, color: '#1890ff', display: 'inline-flex', alignItems: 'center' }}>
                {stats?.total_orders ?? 0}
                <Arrow curr={stats?.total_orders} prev={stats?.total_orders_prev} />
              </span>
            </div>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card style={cardStyle} styles={{ body: { padding: 16 } }}>
            <div>
              <div style={rowLineStyle}>
                <span style={titleTextStyle}>Заказчики</span>
                <span style={{ ...valueAccentStyle, display: 'inline-flex', alignItems: 'center' }}>
                  {stats?.active_customers ?? 0}
                  <Arrow curr={stats?.active_customers} prev={stats?.active_customers_prev} />
                </span>
              </div>
              <div style={{ height: 8 }} />
              <div style={rowLineStyle}>
                <span style={titleTextStyle}>Сотрудники</span>
                <span style={{ ...valueAccentStyle, display: 'inline-flex', alignItems: 'center' }}>
                  {stats?.active_users ?? 0}
                  <Arrow curr={stats?.active_users} prev={stats?.active_users_prev} />
                </span>
              </div>
              <div style={{ height: 8 }} />
              <div style={rowLineStyle}>
                <span style={titleTextStyle}>Поставщики</span>
                <span style={{ ...valueAccentStyle, display: 'inline-flex', alignItems: 'center' }}>
                  {stats?.active_suppliers ?? 0}
                  <Arrow curr={stats?.active_suppliers} prev={stats?.active_suppliers_prev} />
                </span>
              </div>
            </div>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card style={cardStyle} styles={{ body: { padding: 16 } }}>
            <div style={rowLineStyle}>
              <span style={titleTextStyle}>Блюд в меню</span>
              <span style={{ ...valueTextStyle, color: '#faad14', display: 'inline-flex', alignItems: 'center' }}>
                {stats?.menu_items ?? 0}
                <Arrow curr={stats?.menu_items} prev={stats?.menu_items_prev} />
              </span>
            </div>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card style={cardStyle} styles={{ body: { padding: 16 } }}>
            <div style={rowLineStyle}>
              <span style={titleTextStyle}>Общая сумма</span>
              <span style={{ ...valueTextStyle, color: '#f5222d', display: 'inline-flex', alignItems: 'center' }}>
                ₽{stats?.total_amount ?? 0}
                <Arrow curr={stats?.total_amount} prev={stats?.total_amount_prev} />
              </span>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Служба поддержки - второй ряд (только для админа/support) */}
      {(user?.role === 'admin' || user?.role === 'support') && (
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} lg={8}>
            <Card style={cardStyle} styles={{ body: { padding: 16 } }}>
              <div style={rowLineStyle}>
                <span style={titleTextStyle}>Обращений всего</span>
                <span style={{ ...valueTextStyle, color: '#1890ff', display: 'inline-flex', alignItems: 'center' }}>
                  {stats?.support_tickets_total ?? 0}
                  <Arrow curr={stats?.support_tickets_total} prev={stats?.support_tickets_total_prev} />
                </span>
              </div>
            </Card>
          </Col>
          
          <Col xs={24} sm={12} lg={8}>
            <Card style={cardStyle} styles={{ body: { padding: 16 } }}>
              <div style={rowLineStyle}>
                <span style={titleTextStyle}>Без ответа</span>
                <span style={{ ...valueTextStyle, color: '#faad14', display: 'inline-flex', alignItems: 'center' }}>
                  {stats?.support_tickets_unanswered ?? 0}
                  <Arrow curr={stats?.support_tickets_unanswered} prev={stats?.support_tickets_unanswered_prev} />
                </span>
              </div>
            </Card>
          </Col>
          
          <Col xs={24} sm={12} lg={8}>
            <Card style={cardStyle} styles={{ body: { padding: 16 } }}>
              <div style={rowLineStyle}>
                <span style={titleTextStyle}>Закрыто</span>
                <span style={{ ...valueTextStyle, color: '#52c41a', display: 'inline-flex', alignItems: 'center' }}>
                  {stats?.support_tickets_closed ?? 0}
                  <Arrow curr={stats?.support_tickets_closed} prev={stats?.support_tickets_closed_prev} />
                </span>
              </div>
            </Card>
          </Col>
        </Row>
      )}

    </>
  );
};

export default Dashboard;
